import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { paymentsAPI } from 'api/payments'
import type { InvoicePayments, InvoiceSafeTotals } from 'Types/Invoice'
import { branchChanged } from './branchSlice'

interface PaymentsState {
 data: InvoicePayments[]
 loading: boolean
 error: string | null
 pagination: {
 total: number
 per_page: number
 current_page: number
 last_page: number
 to: number
 } | null
 safeTotals: InvoiceSafeTotals | null
 safeTotalsLoading: boolean
}

const initialState: PaymentsState = {
 data: [],
 loading: false,
 error: null,
 pagination: null,
 safeTotals: null,
 safeTotalsLoading: false,
}

export const fetchPayments = createAsyncThunk(
 'payments/fetchPayments',
 async (params: Record<string, unknown> = {}, { rejectWithValue }) => {
 try {
 const response = await paymentsAPI.getAll(params as any)
 return response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.payments.fetchFailed'))
 }
 }
)

export const fetchSafeTotals = createAsyncThunk(
 'payments/fetchSafeTotals',
 async (params: { start_date?: string | null; end_date?: string | null } = {}, { rejectWithValue }) => {
 try {
 const response = await paymentsAPI.getSafeTotals(params)
 return response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.payments.safeTotalsFailed'))
 }
 }
)

const paymentsSlice = createSlice({
 name: 'payments',
 initialState,
 reducers: {
 clearPayments: (state) => {
 state.data = []
 state.error = null
 state.pagination = null
 },
 },
 extraReducers: (builder) => {
 builder
 .addCase(fetchPayments.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(fetchPayments.fulfilled, (state, action) => {
 state.loading = false
 const payload = action.payload as any
 const resultData = payload?.result ?? payload
 const items = resultData?.data ?? (Array.isArray(resultData) ? resultData : [])
 state.data = Array.isArray(items) ? items : []
 const meta = resultData?.meta
 if (meta) {
 state.pagination = {
 total: meta.total ?? 0,
 per_page: meta.per_page ?? 12,
 current_page: meta.current_page ?? 1,
 last_page: meta.last_page ?? 0,
 to: meta.to ?? 0,
 }
 }
 })
 .addCase(fetchPayments.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 .addCase(fetchSafeTotals.pending, (state) => {
 state.safeTotalsLoading = true
 })
 .addCase(fetchSafeTotals.fulfilled, (state, action) => {
 state.safeTotalsLoading = false
 const payload = action.payload as any
 const resultData = payload?.result?.data ?? payload?.data ?? payload
 if (resultData?.totals_by_safe) {
 state.safeTotals = resultData
 } else if (Array.isArray(resultData) && resultData.length > 0) {
 state.safeTotals = resultData[0]
 } else {
 state.safeTotals = resultData
 }
 })
 .addCase(fetchSafeTotals.rejected, (state) => {
 state.safeTotalsLoading = false
 })
 .addCase(branchChanged, (state) => {
 Object.assign(state, initialState)
 })
 },
})

export const { clearPayments } = paymentsSlice.actions
export default paymentsSlice.reducer
